import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
    Alert,
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from "@mui/material";
import { UrlApi } from "../../../../services/apirest";

function parseFechaRechazo(fechaStr) {
    if (!fechaStr) return null;
    const normalized = String(fechaStr).trim().replace(" ", "T");
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
}

function formatoCabeceraHistorial(fechaStr, usuario) {
    const d = parseFechaRechazo(fechaStr);
    const user = String(usuario ?? "").trim() || "—";
    if (!d) {
        return `${String(fechaStr ?? "—")} - ${user}`;
    }
    let s = new Intl.DateTimeFormat("es-AR", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).format(d);
    s = s.replace(/\./g, "").replace(/,/g, " ").replace(/\s+/g, " ").trim();
    const fechaFormateada = s.charAt(0).toUpperCase() + s.slice(1);
    return `${fechaFormateada} - ${user}`;
}

export default function ModalHistorialRechazos({ open, onClose, nroCuit }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open || !nroCuit) return;

        const cuit = encodeURIComponent(String(nroCuit).trim());
        let cancelado = false;
        setLoading(true);
        setError("");
        setItems([]);

        fetch(`${UrlApi}/proveedores/rechazos/${cuit}`)
            .then(async (res) => {
                if (!res.ok) {
                    const text = await res.text().catch(() => "");
                    throw new Error(text || `Error ${res.status}`);
                }
                return res.json();
            })
            .then((data) => {
                if (cancelado) return;
                const lista = Array.isArray(data) ? data : [];
                lista.sort(
                    (a, b) =>
                        (parseFechaRechazo(b.fecha_rechazo)?.getTime() ?? 0) -
                        (parseFechaRechazo(a.fecha_rechazo)?.getTime() ?? 0)
                );
                setItems(lista);
            })
            .catch((e) => {
                if (!cancelado) {
                    setError(e?.message || "No se pudo cargar el historial de rechazos.");
                }
            })
            .finally(() => {
                if (!cancelado) setLoading(false);
            });

        return () => {
            cancelado = true;
        };
    }, [open, nroCuit]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
            <DialogTitle
                sx={{
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pr: 1,
                    borderBottom: 1,
                    borderColor: "divider",
                }}
            >
                Historial de rechazos
                <IconButton aria-label="cerrar" onClick={onClose} edge="end" size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 ,mt: 2}}>
                    En este historial se muestran todos los rechazos realizados sobre este proveedor.
                </Typography>

                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={36} />
                    </Box>
                ) : null}

                {!loading && error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : null}

                {!loading && !error && items.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No hay rechazos registrados para este proveedor.
                    </Typography>
                ) : null}

                {!loading && !error && items.length > 0
                    ? items.map((row) => (
                          <Box key={row.id} sx={{ mb: 2.5 }}>
                              <Typography
                                  variant="body2"
                                  sx={{
                                      color: "primary.main",
                                      fontWeight: 600,
                                      mb: 0.75,
                                  }}
                              >
                                  {formatoCabeceraHistorial(row.fecha_rechazo, row.usuario_rechazo)}
                              </Typography>
                              <Box
                                  sx={{
                                      border: 1,
                                      borderColor: "grey.300",
                                      borderRadius: 1,
                                      p: 1.5,
                                      bgcolor: "grey.50",
                                  }}
                              >
                                  <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.5 }}>
                                      {row.motivo}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                      Mail informado: {Number(row.mail_informado) === 1 ? "Sí" : "No"}
                                  </Typography>
                              </Box>
                          </Box>
                      ))
                    : null}
            </DialogContent>
        </Dialog>
    );
}
